
import { 
  Recycle, 
  Trash2, 
  Droplets, 
  Package, 
  Lightbulb, 
  Hammer, 
  Building2, 
  CircleDot,
  Leaf 
} from "lucide-react";

// Complete mapping of all database values to display labels
const MATERIAL_MAPPING: Record<string, string> = {
  // Known database values
  'non_recyclable': 'Não reciclável',
  'oil': 'Óleo',
  'recycable': 'Reciclável', // Note: typo in database
  'recyclable': 'Reciclável',
  'organic': 'Orgânico',
  'orgânico': 'Orgânico',
  'cardboard': 'Papelão',
  'papelão': 'Papelão',
  'waste': 'Rejeito',
  'comum': 'Comum',
  'eletrônico': 'Eletrônico',
  'madeira': 'Madeira',
  'wood': 'Madeira',
  'metal': 'Metal/Alumínio',
  'aluminio': 'Metal/Alumínio',
  'alumínio': 'Metal/Alumínio',
  'vidro': 'Vidro',
  'glass': 'Vidro',
  'rcc': 'RCC',
  'entulho': 'Entulho',
  'plastic': 'Plástico',
  'plástico': 'Plástico',
  'paper': 'Papel',
  'papel': 'Papel',
  // Oil variations
  'óleo': 'Óleo',
  'óleo usado': 'Óleo',
  'oleo': 'Óleo',
  'oleo usado': 'Óleo',
  // Additional translations
  'rubble': 'Entulho',
  'liquid': 'Líquido',
  'contaminants': 'Contaminantes',
  // Novos materiais mapeados (anteriormente apareciam em inglês)
  'battery': 'Bateria',
  'biodegradable_urban_cleaning': 'Limpeza urbana biodegradável',
  'contaminated_packaging': 'Embalagem contaminada',
  'effluent': 'Efluente',
  'electronics': 'Eletrônico',
  'fluorescent_lamp': 'Lâmpada fluorescente',
  'lamp': 'Lâmpada',
  'metals': 'Metais',
  'rubber_tire': 'Pneu',
  'scrap_metal': 'Sucata metálica',
  'ferrous_scrap': 'Sucata ferrosa',
  'pruning': 'Poda',
  // Materiais que estavam faltando
  'coconuts': 'Coco',
  'coconut': 'Coco',
  'non_recyclable_unspecified': 'Não reciclável não especificado',
  'outros': 'Outros',
  'other': 'Outros',
  'not_specified': 'Não especificado',
  'not specified': 'Não especificado'
};

// Color mapping for materials - FONTE ÚNICA DE VERDADE
// Cores ajustadas para serem visualmente distintas entre materiais semelhantes
// IMPORTANTE: Cada material deve ter uma cor única para facilitar identificação visual
export const MATERIAL_COLORS: Record<string, string> = {
  // Materiais principais
  'Não reciclável': '#9CA3AF',    // cinza médio
  'Óleo': '#F59E0B',              // amarelo/laranja
  'Reciclável': '#10B981',        // verde esmeralda
  'Orgânico': '#84CC16',          // verde lima
  'Papelão': '#D97706',           // laranja escuro
  'Rejeito': '#4B5563',           // cinza azulado escuro
  'Comum': '#6B7280',             // cinza
  'Eletrônico': '#3B82F6',        // azul
  'Madeira': '#92400E',           // marrom escuro
  'Metal/Alumínio': '#64748B',    // cinza azulado
  'Vidro': '#0891B2',             // cyan escuro
  'RCC': '#A8A29E',               // bege acinzentado
  'Entulho': '#78716C',           // marrom acinzentado
  'Plástico': '#EF4444',          // vermelho
  'Papel': '#F97316',             // laranja
  'Líquido': '#0284C7',           // azul sky
  'Contaminantes': '#DC2626',     // vermelho escuro
  
  // Materiais específicos
  'Bateria': '#7C3AED',           // roxo
  'Limpeza urbana biodegradável': '#22C55E', // verde brilhante
  'Embalagem contaminada': '#BE185D', // pink escuro
  'Efluente': '#8B5CF6',          // violeta
  'Lâmpada fluorescente': '#FBBF24', // amarelo brilhante
  'Lâmpada': '#EAB308',           // amarelo dourado
  'Metais': '#71717A',            // cinza neutro
  'Pneu': '#1F2937',              // cinza muito escuro
  'Sucata metálica': '#52525B',   // cinza carvão
  'Sucata ferrosa': '#44403C',    // marrom ferro
  'Poda': '#65A30D',              // verde folha
  
  // Materiais que estavam faltando - novas cores únicas
  'Coco': '#059669',              // verde água (orgânico tropical)
  'Não reciclável não especificado': '#6366F1', // índigo (diferente de "Não reciclável")
  'Outros': '#EC4899',            // rosa vibrante (cor única)
  'Não especificado': '#A78BFA'   // roxo claro (cor única)
};

// Cor padrão para materiais não mapeados
export const DEFAULT_MATERIAL_COLOR = '#CBD5E1'; // cinza claro

// Icon mapping for materials
export const getMaterialIcon = (materialName: string) => {
  const normalizedName = materialName.toLowerCase();
  
  if (normalizedName.includes('reciclável')) return Recycle;
  if (normalizedName.includes('não reciclável')) return Trash2;
  if (normalizedName.includes('orgânico')) return Leaf;
  if (normalizedName.includes('óleo')) return Droplets;
  if (normalizedName.includes('papelão')) return Package;
  if (normalizedName.includes('vidro')) return Lightbulb;
  if (normalizedName.includes('metal') || normalizedName.includes('alumínio')) return Hammer;
  if (normalizedName.includes('entulho') || normalizedName.includes('rcc')) return Building2;
  if (normalizedName.includes('coco')) return Leaf;
  if (normalizedName.includes('poda')) return Leaf;
  if (normalizedName.includes('lâmpada') || normalizedName.includes('lamp')) return Lightbulb;
  if (normalizedName.includes('líquido') || normalizedName.includes('efluente')) return Droplets;
  if (normalizedName.includes('madeira') || normalizedName.includes('wood')) return Package;
  if (normalizedName.includes('plástico') || normalizedName.includes('plastic')) return Package;
  if (normalizedName.includes('papel') || normalizedName.includes('paper')) return Package;
  
  return CircleDot; // Default icon
};

// Normalize material value for consistent lookup
const normalizeMaterialValue = (value: string): string => {
  return value?.toLowerCase().trim() || '';
};

// Get display label for a material value
export const getMaterialDisplayLabel = (value: string): string => {
  if (!value) return 'Não especificado';
  
  const normalizedValue = normalizeMaterialValue(value);
  const mappedValue = MATERIAL_MAPPING[normalizedValue];
  
  if (mappedValue) {
    console.log(`Material mapping: '${value}' -> '${mappedValue}'`);
    return mappedValue;
  }
  
  // Fallback: capitalize first letter for unknown materials
  const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
  console.log(`No mapping found for '${value}', using capitalized: '${capitalized}'`);
  return capitalized;
};

// Get material color - usa MATERIAL_COLORS como fonte única
export const getMaterialColor = (materialLabel: string): string => {
  // Tenta encontrar a cor exata
  if (MATERIAL_COLORS[materialLabel]) {
    return MATERIAL_COLORS[materialLabel];
  }
  
  // Tenta case-insensitive
  const lowerLabel = materialLabel.toLowerCase();
  for (const [key, color] of Object.entries(MATERIAL_COLORS)) {
    if (key.toLowerCase() === lowerLabel) {
      return color;
    }
  }
  
  // Fallback apenas para materiais realmente desconhecidos
  console.warn(`Material sem cor definida: "${materialLabel}" - usando fallback`);
  return DEFAULT_MATERIAL_COLOR;
};

// Get all available material options for filters
export const getMaterialOptions = () => {
  // Get unique display labels
  const uniqueLabels = [...new Set(Object.values(MATERIAL_MAPPING))];
  
  // Sort alphabetically
  uniqueLabels.sort((a, b) => a.localeCompare(b));
  
  return [
    { value: 'all', label: 'Todos os materiais' },
    ...uniqueLabels.map(label => ({
      value: label,
      label: label
    }))
  ];
};

// Get all database values that map to a specific display label
export const getDatabaseValuesForLabel = (displayLabel: string): string[] => {
  return Object.entries(MATERIAL_MAPPING)
    .filter(([_, label]) => label === displayLabel)
    .map(([dbValue, _]) => dbValue);
};

// Check if a material exists in our mapping
export const isMaterialMapped = (value: string): boolean => {
  const normalizedValue = normalizeMaterialValue(value);
  return normalizedValue in MATERIAL_MAPPING;
};

// Export the complete mapping for reference
export const getAllMaterialMappings = () => MATERIAL_MAPPING;
